import React from 'react';
import { clsx } from 'clsx';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useSettingsQueries } from '@/hooks';
import { DOCS_LINKS, isDocsLinkKey, type DocsLinkKey } from './docs-links';

interface Props {
  /** Either a known key from DOCS_LINKS or a raw absolute URL starting
   * with `http`. Anything else logs a console.warn and renders nothing. */
  to: DocsLinkKey | string;
  /** Visible / accessible label. Defaults to "Help". When `iconOnly` is
   * true, label is exposed via `aria-label`. */
  label?: string;
  /** Render only the question-mark icon. */
  iconOnly?: boolean;
  className?: string;
}

const resolveHref = (to: string): string | null => {
  if (isDocsLinkKey(to)) {
    // Empty placeholder (LR-316 pre-ship state) — render nothing without
    // emitting a warning. Once the Confluence URLs land in DOCS_LINKS the
    // link renders automatically, no call-site change required.
    return DOCS_LINKS[to] || null;
  }
  if (to.startsWith('http://') || to.startsWith('https://')) return to;
   
  console.warn(
    `[HelpLink] unknown docs key or non-http URL: ${to}. Pass a key from DOCS_LINKS or an absolute URL.`,
  );
  return null;
};

export const HelpLink: React.FC<Props> = ({
  to,
  label = 'Help',
  iconOnly = false,
  className,
}) => {
  const { data: settings } = useSettingsQueries();
  const href = resolveHref(to);
  if (!href) return null;

  // Relative URLs point at the internal mkdocs KB served under /docs/ on the
  // same host. Prod's nginx 404s that route by design, so such links render
  // only when the stand opts in via the server-side `docs_enabled` flag
  // (LR-593). Hidden while settings are loading or on an older backend
  // without the field — a missing link beats a link into a 404. Absolute
  // http(s) URLs (e.g. future Confluence pages, LR-316) don't depend on the
  // local /docs/ route and stay unaffected.
  if (href.startsWith('/') && settings?.docs_enabled !== true) return null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={iconOnly ? label : undefined}
      className={clsx(
        'inline-flex items-center gap-1 text-gray-500 hover:text-primary-700',
        iconOnly ? 'p-1' : 'text-sm font-medium',
        className,
      )}
    >
      <QuestionMarkCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
      {!iconOnly && <span>{label}</span>}
    </a>
  );
};
